import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, RotateCcw, Save } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  Callout, Checkbox, FormField, Input, Label, NativeSelect, Radio, Textarea,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import apiClient from "../../utils/apiClient";
import { formatDate } from "../../utils/format";
import {
  COMMON_RATES, DEFAULT_DESIGNATION, DEFAULT_SAC, MAX_RATE, gstBreakdown, gstinLooksRight, gstinProblem,
  normaliseGstin, stateLabel, stateOf, validRate,
} from "../../utils/feeInvoice";
import { formatCurrency } from "../Fees/invoice/invoiceFormat";
import InvoicePreview from "./invoice/InvoicePreview";
import SignatureEditor from "./invoice/SignatureEditor";
import SettingsSection from "./SettingsSection";

const TITLE_OPTIONS = [
  { value: "invoice", label: "Invoice", description: "Printed as “Tax invoice” on payments GST applies to." },
  { value: "receipt", label: "Receipt", description: "A plain payment receipt." },
  { value: "fee_receipt", label: "Fee receipt", description: "The usual word at schools and coaching centres." },
];

const RATE_OPTIONS = [
  ...COMMON_RATES.map((r) => ({ value: String(r), label: r === 18 ? "18% — training and coaching" : `${r}%` })),
  { value: "other", label: "Another rate…" },
];

/** Today in India, as the server counts days: 'YYYY-MM-DD'. */
const todayKey = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(new Date());

const toForm = (payload) => {
  const s = payload.settings;
  return {
    title: s.title,
    showLogo: s.showLogo,
    showGstin: s.showGstin,
    showBalance: s.showBalance,
    showAmountInWords: s.showAmountInWords,
    gst: { enabled: s.gst.enabled, rate: String(s.gst.rate), pricing: s.gst.pricing, sac: s.gst.sac, from: s.gst.from },
    signature: { enabled: s.signature.enabled, name: s.signature.name, designation: s.signature.designation },
    notes: s.notes,
    gstin: payload.institute.gstin || "",
  };
};

const sameRule = (a, b) => Number(a.rate) === Number(b.rate) && a.pricing === b.pricing && a.sac === b.sac;

/**
 * Settings → Invoices — what every student fee invoice says beyond the payment:
 * its title, the logo, the GSTIN, GST (rate, whether fees include it, SAC, from
 * when), the authorised signatory, amount in words, the balance, and notes.
 *
 * One form, saved with its button. The preview beside it is the real invoice
 * PDF, rendered by the server from the unsaved form on every change — the same
 * renderer as every fee invoice, so preview and invoice cannot differ. The
 * signature image alone is saved the moment it is added. With nothing changed,
 * invoices are exactly what they always were.
 *
 * Rules: vc_be/utils/feeInvoice (twin: utils/feeInvoice).
 */
export default function InvoiceSettings({ showToast }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [otherRate, setOtherRate] = useState(false);
  const [applyFrom, setApplyFrom] = useState("today"); // when a saved GST rule changes
  const [busy, setBusy] = useState(null); // 'save'
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState(false);

  const adopt = useCallback((payload) => {
    const next = toForm(payload);
    setData(payload);
    setForm(next);
    setSaved(next);
    setOtherRate(!COMMON_RATES.includes(Number(next.gst.rate)));
    setApplyFrom("today");
  }, []);

  useEffect(() => {
    let live = true;
    apiClient.get("/invoice-settings")
      .then(({ data: payload }) => { if (live) adopt(payload); })
      .catch(() => { if (live) setLoadError(true); });
    return () => { live = false; };
  }, [adopt]);

  const dirty = useMemo(() => Boolean(form && saved && JSON.stringify(form) !== JSON.stringify(saved)), [form, saved]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  if (loadError) return <Callout tone="danger">Couldn&apos;t load your invoice settings. Please reload the page.</Callout>;
  if (!form) return <Shimmer height={52} count={6} />;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setGst = (patch) => setForm((f) => ({ ...f, gst: { ...f.gst, ...patch } }));
  const setSignature = (patch) => setForm((f) => ({ ...f, signature: { ...f.signature, ...patch } }));

  const today = todayKey();
  const gstin = normaliseGstin(form.gstin);
  const gstinError = gstinProblem(gstin);
  const gstinWarning = gstin && !gstinError && !gstinLooksRight(gstin);
  const ownState = gstin && !gstinError ? stateOf(gstin.slice(0, 2)) : null;
  const rate = validRate(form.gst.rate);
  const rateError = form.gst.enabled && !rate ? `Enter a rate above 0% and up to ${MAX_RATE}%.` : null;
  const sacError = form.gst.sac && !/^\d{4,8}$/.test(form.gst.sac) ? "A SAC code is 4 to 8 digits." : null;
  const needsGstin = form.gst.enabled && !gstin;
  // A saved rule edited in place: ask whether it starts today or corrects the past.
  const ruleChanged = saved.gst.enabled && form.gst.enabled && !sameRule(saved.gst, form.gst);
  const switchedOff = saved.gst.enabled && !form.gst.enabled;
  const blocked = Boolean(gstinError || rateError || sacError || needsGstin);
  const issuer = data.institute.legalName || data.institute.name;

  const toggleGst = (enabled) => {
    setGst({ enabled, from: enabled ? (saved.gst.enabled ? saved.gst.from : today) : "" });
  };

  const body = () => {
    const out = {
      title: form.title,
      showLogo: form.showLogo,
      showGstin: form.showGstin,
      showBalance: form.showBalance,
      showAmountInWords: form.showAmountInWords,
      gst: { ...form.gst, rate: rate ?? form.gst.rate },
      signature: form.signature,
      notes: form.notes,
    };
    if (ruleChanged) out.gst.from = applyFrom === "today" ? today : saved.gst.from;
    if (gstin !== normaliseGstin(saved.gstin)) out.gstin = gstin;
    return out;
  };

  const save = async () => {
    if (blocked) return;
    setBusy("save");
    setError("");
    try {
      const { data: payload } = await apiClient.put("/invoice-settings", body());
      adopt(payload);
      showToast?.("Invoice settings saved. Every invoice — PDF, print and email — now follows them.");
    } catch (e) {
      setError(e?.response?.data?.message || "Couldn't save your invoice settings. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  // What the preview renders: the form as Save would send it, except that a
  // field still being typed (a half GSTIN, an empty rate) shows the saved value
  // instead of stopping the preview.
  const previewBody = () => {
    const out = body();
    out.gstin = gstinError ? normaliseGstin(saved.gstin) : gstin;
    out.gst = { ...out.gst, rate: rate ?? Number(saved.gst.rate), sac: sacError ? saved.gst.sac : out.gst.sac };
    return out;
  };

  const signatureSaved = (payload) => {
    setData(payload);
  };

  const exampleOnTop = rate ? gstBreakdown({ amount: 10000, rate, pricing: "exclusive" }) : null;
  const exampleIn = rate ? gstBreakdown({ amount: 10000, rate, pricing: "inclusive" }) : null;

  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>
          <span className="tw-inline-flex tw-items-center tw-gap-2">
            <FileText size={18} aria-hidden="true" /> Invoices
          </span>
        </AppCardTitle>
        <AppCardDescription>
          What every fee invoice says — the one you print, download, email and students download from My Fees.
          Changes apply to every invoice once you save, except GST, which starts from the day you choose.
        </AppCardDescription>
      </AppCardHeader>

      <AppCardContent>
        <div className="tw-grid tw-gap-8 xl:tw-grid-cols-[minmax(0,1fr)_minmax(0,30rem)]">
          <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-5">
            <SettingsSection title="What it's called">
              <div className="tw-grid tw-gap-3 md:tw-grid-cols-3" role="radiogroup" aria-label="Document title">
                {TITLE_OPTIONS.map((o) => (
                  <Radio
                    key={o.value}
                    variant="card"
                    name="invoice-title"
                    value={o.value}
                    checked={form.title === o.value}
                    onChange={() => set({ title: o.value })}
                    label={o.label}
                    description={o.description}
                  />
                ))}
              </div>
              {form.gst.enabled && form.title !== "invoice" && (
                <Callout
                  tone="warning"
                  action={<AppButton size="xs" variant="outline" onClick={() => set({ title: "invoice" })}>Use Invoice</AppButton>}
                >
                  A GST-registered business must title the document “Tax invoice”.
                </Callout>
              )}
            </SettingsSection>

            <SettingsSection title="Your details" description="Your name, address, email and phone come from Settings → General.">
              <Checkbox
                checked={form.showLogo}
                disabled={!data.logo.url}
                onChange={(e) => set({ showLogo: e.target.checked })}
                label="Show your logo"
                description={!data.logo.url
                  ? "Upload a logo in Settings → Branding first."
                  : data.logo.printable
                    ? "Your logo from Settings → Branding, above your name."
                    : "Shown on the page, but its file type can't go into the PDF. Upload a PNG or JPG in Settings → Branding."}
              />
              <div className="tw-grid tw-gap-4 md:tw-grid-cols-2">
                <FormField
                  label="GSTIN"
                  error={gstinError}
                  hint={gstinWarning
                    ? "Double-check this GSTIN — its last character doesn't match the rest."
                    : ownState
                      ? `Registered in ${stateLabel(ownState)}.`
                      : "Your 15-character GST number, if you are registered."}
                >
                  <Input
                    value={form.gstin}
                    maxLength={20}
                    autoComplete="off"
                    placeholder="29ABCDE1234F1Z5"
                    onChange={(e) => set({ gstin: e.target.value.toUpperCase() })}
                  />
                </FormField>
                <div className="tw-flex tw-items-end tw-pb-1">
                  <Checkbox
                    checked={form.showGstin}
                    disabled={!gstin}
                    onChange={(e) => set({ showGstin: e.target.checked })}
                    label="Print the GSTIN on invoices"
                  />
                </div>
              </div>
            </SettingsSection>

            <SettingsSection title="GST" description="For GST-registered institutes. Turns invoices into tax invoices with the tax shown.">
              <Checkbox
                checked={form.gst.enabled}
                onChange={(e) => toggleGst(e.target.checked)}
                label="Charge GST on fees"
                description="Each invoice shows the taxable value, CGST + SGST — or IGST for a buyer in another state — and the total."
              />

              {switchedOff && (
                <Callout tone="info">
                  Payments from today won&apos;t show GST. Invoices for earlier payments keep the GST they were issued with.
                </Callout>
              )}

              {form.gst.enabled && (
                <div className="tw-flex tw-flex-col tw-gap-4 tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-4">
                  {needsGstin && (
                    <Callout tone="warning">Add your GSTIN above — a tax invoice must carry it.</Callout>
                  )}
                  <div className="tw-grid tw-gap-4 md:tw-grid-cols-2">
                    <FormField label="GST rate" error={rateError}>
                      <NativeSelect
                        value={otherRate ? "other" : String(Number(form.gst.rate))}
                        onChange={(e) => {
                          if (e.target.value === "other") { setOtherRate(true); return; }
                          setOtherRate(false);
                          setGst({ rate: e.target.value });
                        }}
                        options={RATE_OPTIONS}
                      />
                    </FormField>
                    {otherRate ? (
                      <FormField label="Rate (%)" hint={`Above 0 and up to ${MAX_RATE}.`}>
                        <Input
                          type="number"
                          inputMode="decimal"
                          min="0.01"
                          max={MAX_RATE}
                          step="0.01"
                          value={form.gst.rate}
                          onChange={(e) => setGst({ rate: e.target.value })}
                          trailing="%"
                          trailingPad="text"
                        />
                      </FormField>
                    ) : (
                      <FormField
                        label="SAC code"
                        error={sacError}
                        hint="999293 — commercial training and coaching. 999294 — other education and training."
                      >
                        <Input
                          value={form.gst.sac}
                          inputMode="numeric"
                          maxLength={8}
                          placeholder={DEFAULT_SAC}
                          onChange={(e) => setGst({ sac: e.target.value.replace(/\D/g, "") })}
                        />
                      </FormField>
                    )}
                  </div>
                  {otherRate && (
                    <FormField
                      label="SAC code"
                      error={sacError}
                      hint="999293 — commercial training and coaching. 999294 — other education and training."
                    >
                      <Input
                        value={form.gst.sac}
                        inputMode="numeric"
                        maxLength={8}
                        placeholder={DEFAULT_SAC}
                        onChange={(e) => setGst({ sac: e.target.value.replace(/\D/g, "") })}
                      />
                    </FormField>
                  )}

                  <div className="tw-flex tw-flex-col tw-gap-2">
                    <Label>Your fee amounts…</Label>
                    <div className="tw-grid tw-gap-3 md:tw-grid-cols-2" role="radiogroup" aria-label="Your fee amounts">
                      <Radio
                        variant="card"
                        name="gst-pricing"
                        value="inclusive"
                        checked={form.gst.pricing === "inclusive"}
                        onChange={() => setGst({ pricing: "inclusive" })}
                        label="Include GST"
                        description={exampleIn
                          ? `A ${formatCurrency(10000)} payment is ${formatCurrency(exampleIn.taxable)} + ${formatCurrency(exampleIn.tax)} GST.`
                          : "GST is taken out of the amount paid."}
                      />
                      <Radio
                        variant="card"
                        name="gst-pricing"
                        value="exclusive"
                        checked={form.gst.pricing === "exclusive"}
                        onChange={() => setGst({ pricing: "exclusive" })}
                        label="Are before GST"
                        description={exampleOnTop
                          ? `A ${formatCurrency(10000)} fee is invoiced as ${formatCurrency(10000)} + ${formatCurrency(exampleOnTop.tax)} GST = ${formatCurrency(exampleOnTop.total)}.`
                          : "GST is added on top of the fee."}
                      />
                    </div>
                    {form.gst.pricing === "exclusive" && (
                      <p className="tw-m-0 tw-text-xs tw-text-content-muted">
                        For payments you record. Online and UPI QR payments collect exactly the fee, so their invoices
                        treat the amount paid as including GST.
                      </p>
                    )}
                  </div>

                  {ruleChanged ? (
                    <div className="tw-flex tw-flex-col tw-gap-2">
                      <Label>Apply this change to</Label>
                      <div className="tw-grid tw-gap-3 md:tw-grid-cols-2" role="radiogroup" aria-label="Apply this change to">
                        <Radio
                          variant="card"
                          name="gst-apply"
                          value="today"
                          checked={applyFrom === "today"}
                          onChange={() => setApplyFrom("today")}
                          label="Payments from today"
                          description={`Earlier invoices keep ${saved.gst.rate}%, ${saved.gst.pricing === "exclusive" ? "added on top" : "included in the fee"}.`}
                        />
                        <Radio
                          variant="card"
                          name="gst-apply"
                          value="correct"
                          checked={applyFrom === "correct"}
                          onChange={() => setApplyFrom("correct")}
                          label={`Every payment since ${formatDate(saved.gst.from)}`}
                          description="To correct a mistake: earlier invoices change too when downloaded again."
                        />
                      </div>
                    </div>
                  ) : (
                    <FormField
                      label="Applies to payments from"
                      hint={saved.gst.enabled
                        ? "Invoices for payments before this day show no GST."
                        : "Usually today, or the day your GST registration began. Invoices for earlier payments don't change."}
                    >
                      <Input
                        type="date"
                        value={form.gst.from}
                        onChange={(e) => setGst({ from: e.target.value })}
                      />
                    </FormField>
                  )}

                  {rate && (
                    <p className="tw-m-0 tw-text-xs tw-text-content-muted">
                      {ownState
                        ? `Students in ${ownState.name} are charged CGST ${rate / 2}% + SGST ${rate / 2}%. For a student or company in another state, set the place of supply on their invoice and it becomes IGST ${rate}%.`
                        : `Charged as CGST ${rate / 2}% + SGST ${rate / 2}%, or IGST ${rate}% for a buyer in another state.`}
                    </p>
                  )}
                </div>
              )}
            </SettingsSection>

            <SettingsSection title="Authorised signatory">
              <Checkbox
                checked={form.signature.enabled}
                onChange={(e) => setSignature({ enabled: e.target.checked })}
                label="Sign every invoice"
                description={`Adds “For ${issuer}”, the signature and the signatory's name and designation at the foot. Without a signature image there's room to sign printed copies by hand.`}
              />
              {form.signature.enabled && (
                <div className="tw-flex tw-flex-col tw-gap-4 tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-4">
                  <div className="tw-flex tw-flex-col tw-gap-2">
                    <Label>Signature</Label>
                    <SignatureEditor
                      signatureUrl={data.signatureUrl}
                      hasImage={data.settings.signature.hasImage}
                      onSaved={signatureSaved}
                      showToast={showToast}
                    />
                  </div>
                  <div className="tw-grid tw-gap-4 md:tw-grid-cols-2">
                    <FormField label="Signatory's name" hint="Optional — printed under the signature.">
                      <Input
                        value={form.signature.name}
                        maxLength={data.limits.signatoryName}
                        placeholder="Priya Nair"
                        onChange={(e) => setSignature({ name: e.target.value })}
                      />
                    </FormField>
                    <FormField label="Designation" hint={`Empty prints “${DEFAULT_DESIGNATION}”.`}>
                      <Input
                        value={form.signature.designation}
                        maxLength={data.limits.designation}
                        placeholder={DEFAULT_DESIGNATION}
                        onChange={(e) => setSignature({ designation: e.target.value })}
                      />
                    </FormField>
                  </div>
                </div>
              )}
            </SettingsSection>

            <SettingsSection title="More on the invoice">
              <Checkbox
                checked={form.showAmountInWords}
                onChange={(e) => set({ showAmountInWords: e.target.checked })}
                label="Amount in words"
                description="e.g. “Rupees Eleven Thousand Eight Hundred Only”."
              />
              <Checkbox
                checked={form.showBalance}
                onChange={(e) => set({ showBalance: e.target.checked })}
                label="The student's balance"
                description="Total fees, paid so far and still to pay."
              />
              <FormField
                label="Terms & notes"
                hint={`Printed at the foot of every invoice — refund policy, bank details, jurisdiction. ${form.notes.length} / ${data.limits.notes}`}
              >
                <Textarea
                  rows={3}
                  value={form.notes}
                  maxLength={data.limits.notes}
                  placeholder="Fees once paid are not refundable or transferable. Subject to Bengaluru jurisdiction."
                  onChange={(e) => set({ notes: e.target.value })}
                />
              </FormField>
            </SettingsSection>

            {error && <Callout tone="danger" role="alert">{error}</Callout>}

            <div className="tw-sticky tw-bottom-0 tw-z-10 tw--mx-1 tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-bg-surface-raised tw-px-1 tw-py-3">
              <AppButton onClick={save} disabled={!dirty || blocked || busy === "save"}>
                <Save size={16} /> {busy === "save" ? "Saving…" : "Save invoice settings"}
              </AppButton>
              {dirty && (
                <AppButton variant="ghost" onClick={() => adopt(data)} disabled={busy === "save"}>
                  <RotateCcw size={14} /> Discard changes
                </AppButton>
              )}
              <span className="tw-text-xs tw-text-content-muted" aria-live="polite">
                {dirty ? "You have unsaved changes." : "All changes saved."}
              </span>
            </div>
          </div>

          <div className="tw-min-w-0 xl:tw-sticky xl:tw-top-4 xl:tw-self-start">
            {/* A new or removed signature changes no form field, so it is part of the key. */}
            <InvoicePreview body={previewBody()} refreshKey={data.settings.signature.hasImage ? data.signatureUrl : "none"} />
          </div>
        </div>
      </AppCardContent>
    </AppCard>
  );
}
